import { authnClient } from '@frontend/auth';
import { clearLastVisitedPage, getJTI, getWeaveToken } from '@frontend/auth-helpers';
import { sentry } from '@frontend/tracking';
import { signOut } from '../helpers/sign-out';
import {
  type RefreshAccessTokenDecision,
  isTransientAccessTokenRefreshError,
} from '../utils/request-access-token-refresh';

export const createRefreshTokenMiddleware = (deps: {
  setAuthorizationHeader: (token: string | undefined, jtiToken: string | undefined) => void;
  alerts: { error: (msg: string) => void };
  t: (msg: string) => string;
  triggerInactiveSignOut: () => void;
  getCurrentUseJti: () => boolean;
  setUseJti: (useJti: boolean) => void;
  refreshAccessToken: () => Promise<RefreshAccessTokenDecision>;
}) => {
  return async (req: Request): Promise<Request> => {
    const useJTI = await authnClient.shouldUseJTI();
    if (deps.getCurrentUseJti() !== useJTI) {
      deps.setUseJti(useJTI);
    }

    let refreshDecision: RefreshAccessTokenDecision | undefined;
    try {
      refreshDecision = await deps.refreshAccessToken(); // NOTE strategy owns retry + preconditions
      sentry.addBreadcrumb({ message: `[Refresh middleware] ${refreshDecision.result}` });

      if (refreshDecision.result === 'signout') {
        deps.triggerInactiveSignOut();
      }

      if (refreshDecision.result === 'refreshed') {
        req.headers.set(
          'Authorization',
          `Bearer ${
            useJTI ? refreshDecision.tokens.jtiToken ?? refreshDecision.tokens.token : refreshDecision.tokens.token
          }`
        );
        deps.setAuthorizationHeader(refreshDecision.tokens.token || '', refreshDecision.tokens.jtiToken || '');
      }
    } catch (error: any) {
      if (isTransientAccessTokenRefreshError(error)) {
        sentry.addBreadcrumb({ message: `[Refresh middleware] transient error` });
        // Don't return early — fall through to header sync from local storage
      } else {
        if (error?.status >= 500) {
          sentry.error({
            topic: 'auth',
            team: 'users-infra',
            error,
            addContext: { name: 'refresh-token', context: { source: 'request-middleware', status: error?.status } },
          });
        }
        deps.alerts.error(deps.t("You've been signed out"));
        clearLastVisitedPage();
        return signOut().then(() => req);
      }
    }

    // Sync the outgoing request header from local storage, but skip if we just
    // refreshed — in that case the header is already set from the fresh tokens
    // and local storage may not yet reflect the update (especially in Electron
    // where the cache updates async via IPC).
    if (refreshDecision?.result !== 'refreshed') {
      const currAuthHeader = req.headers.get('Authorization');
      if (useJTI) {
        const localStorageJtiToken = getJTI();
        if (currAuthHeader !== `Bearer ${localStorageJtiToken}`) {
          req.headers.set('Authorization', `Bearer ${localStorageJtiToken}`);
        }
      } else {
        const localStorageWeaveToken = getWeaveToken();
        if (currAuthHeader !== `Bearer ${localStorageWeaveToken}`) {
          req.headers.set('Authorization', `Bearer ${localStorageWeaveToken}`);
        }
      }
    }

    return req;
  };
};

export const createResponseInterceptor401Middleware = (deps: {
  getCurrentUseJti: () => boolean;
  getJTI: () => string | undefined;
  getWeaveToken: () => string | undefined;
  setAuthorizationHeader: (token: string, jtiToken: string) => void;
  setUseJti: (useJti: boolean) => void;
}) => {
  return async function responseInterceptor401(response: Response, requestCopy: Request): Promise<Response> {
    if (response.ok) {
      return Promise.resolve(response);
    }

    const useJTI = await authnClient.shouldUseJTI();
    if (deps.getCurrentUseJti() !== useJTI) {
      deps.setUseJti(useJTI);
    }

    if (response.status === 401) {
      try {
        const refreshResponse = await authnClient.refreshWeaveToken();
        deps.setAuthorizationHeader(refreshResponse.token || '', refreshResponse.jtiToken || '');
        const currAuthHeader = requestCopy.headers.get('Authorization');
        const weaveToken = refreshResponse.token;
        const jtiToken = refreshResponse.jtiToken;
        const newAuthHeader = `Bearer ${useJTI ? jtiToken : weaveToken}`;
        if (currAuthHeader !== newAuthHeader) {
          requestCopy.headers.set('Authorization', newAuthHeader);
        }
      } catch (e) {
        // network errors like internet disconnect will throw a TypeError, while a 401 from the server will not throw an error, it will just return a response with status 401
        const isNetworkError = e instanceof TypeError;
        if (!isNetworkError) {
          sentry.error({
            topic: 'auth:refresh-token',
            error: e,
            team: 'users-infra',
            addContext: {
              name: 'refresh-token',
              context: { source: 'response-interceptor-401', url: requestCopy.url },
            },
          });
        }
      }
    }
    return Promise.resolve(response);
  };
};
